import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search,
  Error as ErrorIcon,
  Refresh,
  ExpandMore,
  Description as DescriptionIcon,
  Code as CodeIcon,
  Schedule,
  Assignment,
  Http,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';

const FocusErrors = () => {
  const { actions } = useApp();
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedError, setSelectedError] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchErrors();
  }, []);

  const fetchErrors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/focus-errors');
      if (response.data.success) {
        setErrors(response.data.errors || []);
      }
    } catch (error) {
      actions.showSnackbar('Error fetching focus errors', 'error');
      console.error('Error fetching focus errors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchErrors();
    actions.showSnackbar('Errors refreshed', 'success');
  };

  const handleViewDetails = (error) => {
    setSelectedError(error);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedError(null);
    setRetrying(false);
    setDeleting(false);
  };

  const handleDelete = async () => {
    if (!selectedError || !selectedError.id) {
      actions.showSnackbar('Error: Invalid error data', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this error entry? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      const response = await api.post(`/focus-errors/${selectedError.id}/delete`);
      
      if (response.data.success) {
        actions.showSnackbar('Error entry deleted successfully', 'success');
        // Close modal and refresh the error list
        handleCloseDetailModal();
        await fetchErrors();
      } else {
        actions.showSnackbar(response.data.message || 'Delete failed', 'error');
      }
    } catch (error) {
      actions.showSnackbar(
        error.response?.data?.message || 'Error deleting entry',
        'error'
      );
      console.error('Error deleting entry:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleRetry = async () => {
    if (!selectedError || !selectedError.id) {
      actions.showSnackbar('Error: Invalid error data', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to retry this FOCUS request?')) {
      return;
    }

    try {
      setRetrying(true);
      const response = await api.post(`/focus-errors/${selectedError.id}/retry`);
      
      if (response.data.success) {
        actions.showSnackbar('Request retried successfully!', 'success');
        // Refresh the error list to show updated status
        await fetchErrors();
        // Update the selected error with new status
        const updatedError = await api.get(`/focus-errors/${selectedError.id}`);
        if (updatedError.data.success) {
          setSelectedError(updatedError.data.error);
        }
      } else {
        actions.showSnackbar(response.data.message || 'Retry failed', 'error');
        // Refresh to get updated status (even if failed)
        await fetchErrors();
        const updatedError = await api.get(`/focus-errors/${selectedError.id}`);
        if (updatedError.data.success) {
          setSelectedError(updatedError.data.error);
        }
      }
    } catch (error) {
      actions.showSnackbar(
        error.response?.data?.message || 'Error retrying request',
        'error'
      );
      console.error('Error retrying request:', error);
      // Refresh to get updated status
      await fetchErrors();
      try {
        const updatedError = await api.get(`/focus-errors/${selectedError.id}`);
        if (updatedError.data.success) {
          setSelectedError(updatedError.data.error);
        }
      } catch (e) {
        // Ignore fetch error
      }
    } finally {
      setRetrying(false);
    }
  };

  const getRetryStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'retried':
        return 'info';
      case 'failed':
        return 'error';
      case 'success':
        return 'success';
      default:
        return 'default';
    }
  };

  const getFailedStepLabel = (step) => {
    const labels = {
      order_creation: 'Order Creation',
      status_update: 'Status Update',
      delivery_screen: 'Delivery Screen',
      delivery_update: 'Delivery Update',
      cancellation: 'Cancellation',
      sales_return: 'Sales Return'
    };
    return labels[step] || step;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  // Filter errors based on search term
  const filteredErrors = errors.filter(error => {
    const matchesSearch = 
      error.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      error.errorDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading focus errors...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ErrorIcon sx={{ mr: 2, color: 'error.main' }} />
            <Typography variant="h4" component="h1">
              FOCUS Errors
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            startIcon={<Refresh />}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by order ID, description, or endpoint..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 500 }}
          />
        </Box>

        {/* Stats */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total: ${errors.length}`} 
            color="default" 
            sx={{ fontWeight: 'bold' }}
          />
          <Chip 
            label={`Pending: ${errors.filter(e => e.retryStatus === 'pending').length}`} 
            color="warning" 
          />
          <Chip 
            label={`Failed: ${errors.filter(e => e.retryStatus === 'failed').length}`} 
            color="error" 
          />
          <Chip 
            label={`Success: ${errors.filter(e => e.retryStatus === 'success').length}`} 
            color="success" 
          />
        </Box>

        {/* Errors Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Actions</strong></TableCell>
                <TableCell><strong>Order ID</strong></TableCell>
                <TableCell><strong>Failed Step</strong></TableCell>
                <TableCell><strong>Error Description</strong></TableCell>
                <TableCell><strong>Status Code</strong></TableCell>
                <TableCell><strong>Retry Status</strong></TableCell>
                <TableCell><strong>Retry Count</strong></TableCell>
                <TableCell><strong>Created At</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredErrors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm
                        ? 'No errors found matching your search.'
                        : 'No focus errors found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredErrors.map((error) => (
                  <TableRow key={error.id} hover>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewDetails(error)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {error.orderId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getFailedStepLabel(error.failedStep)} 
                        size="small"
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 300, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={error.errorDescription}
                      >
                        {error.errorDescription}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {error.statusCode ? (
                        <Chip 
                          label={error.statusCode} 
                          size="small"
                          color={error.statusCode >= 500 ? 'error' : error.statusCode >= 400 ? 'warning' : 'default'}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={error.retryStatus} 
                        color={getRetryStatusColor(error.retryStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {error.retryCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {formatDate(error.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Detail Modal */}
      <Dialog 
        open={detailModalOpen} 
        onClose={handleCloseDetailModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ErrorIcon color="error" />
            <Typography variant="h5">
              Error Details - {selectedError?.orderId}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedError && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
              {/* Basic Information */}
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assignment color="primary" />
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Order ID</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedError.orderId}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Failed Step</Typography>
                      <Chip 
                        label={getFailedStepLabel(selectedError.failedStep)} 
                        color="secondary"
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Endpoint</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                        {selectedError.endpoint || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Status Code</Typography>
                      {selectedError.statusCode ? (
                        <Chip 
                          label={selectedError.statusCode} 
                          color={selectedError.statusCode >= 500 ? 'error' : 'warning'}
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>N/A</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Retry Status</Typography>
                      <Chip 
                        label={selectedError.retryStatus} 
                        color={getRetryStatusColor(selectedError.retryStatus)}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Retry Count</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{selectedError.retryCount || 0}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Error Description</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {selectedError.errorDescription}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Timestamps */}
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule color="primary" />
                    Timestamps
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {formatDate(selectedError.createdAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Last Retry At</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {formatDate(selectedError.lastRetryAt) || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Payload */}
              {selectedError.payload && Object.keys(selectedError.payload).length > 0 && (
                <Card elevation={1}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CodeIcon color="primary" />
                      Request Payload
                    </Typography>
                    <Box sx={{ 
                      backgroundColor: 'grey.100', 
                      p: 2, 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 400
                    }}>
                      <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedError.payload, null, 2)}
                      </pre>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Response Data */}
              {selectedError.responseData && Object.keys(selectedError.responseData).length > 0 && (
                <Card elevation={1}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon color="primary" />
                      Response Data
                    </Typography>
                    <Box sx={{ 
                      backgroundColor: 'grey.100', 
                      p: 2, 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 400
                    }}>
                      <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedError.responseData, null, 2)}
                      </pre>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button
            onClick={handleRetry}
            variant="contained"
            color="primary"
            disabled={retrying || deleting || selectedError?.retryStatus === 'success'}
            startIcon={!retrying && <Refresh />}
          >
            {retrying ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Retrying...
              </Box>
            ) : (
              'Retry Request'
            )}
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={retrying || deleting}
            startIcon={!deleting && <DeleteIcon />}
          >
            {deleting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Deleting...
              </Box>
            ) : (
              'Delete'
            )}
          </Button>
          <Button onClick={handleCloseDetailModal} variant="contained" disabled={retrying || deleting}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FocusErrors;
